import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { initialize } from 'redux-form'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { FORMS } from '../../utils/enums'
import { history } from '../../utils/history'
import StoreItemForm from './forms/StoreItemForm'
import {
	createStoreItems, getStoreItem, IStoreItemForm, updateStoreItem
} from '../../reducers/storeItems/actions'
import { IComputedMatch } from '../../types/interfaces'
import { RootState } from '../../reducers'

type Props = {
	computedMatch: IComputedMatch<{ id: number }>;
};

const StoreItemDetail = (props: Props) => {
	const { computedMatch } = props
	const { t } = useTranslation('pages')
	const dispatch = useDispatch()

	// --- 1) Select exactly the “detail” slice of state that has isLoading and data:
	const currentItem = useSelector(
		(state: RootState) => state.storeItems.detail
	)
	const isLoading = currentItem?.isLoading ?? false

	// --- 2) Track “id” in local component state. (null = “create new”)
	const [id, setID] = useState<number | null>(
		computedMatch.params.id || null
	)

	// --- 3) Helper to initialize the form to “empty fields”:
	const initializeEmptyForm = () => {
		const initValues: IStoreItemForm = {
			id: 0,
			name: '',
			nameSlug: '',
			isLiquid: false,
			price: null,
			isReplaceable: false,
			idealStockPercentage: 0,
			activeJobs: 0,
			allAvailableStock: 0,
			updatedAt: '-',
			amountOfStockInAddresses: []
		}
		dispatch(initialize(FORMS.STORE_ITEM_FORM, initValues))
	}

	// --- 4) Helper to initialize the form with “detail” fields:
	const initializeDetailForm = (data: IStoreItemForm) => {
		dispatch(initialize(FORMS.STORE_ITEM_FORM, data))
	}

	// --- 5) useEffect: whenever “id” changes, either fetch detail or set empty
	useEffect(() => {
		if (id !== null) {
			dispatch(
				getStoreItem(id, (response) => {
					const d = response.data
					const initValues: IStoreItemForm = {
						id: d.id,
						isLiquid: d.isLiquid,
						price: d.price,
						isReplaceable: d.isReplaceable,
						updatedAt:
							moment(d.updatedAt).format('D. MMM YYYY HH:mm') || '-',
						allAvailableStock: d.allAvailableStock,
						activeJobs: d.activeJobs,
						idealStockPercentage: d.idealStockPercentage,
						amountOfStockInAddresses: d.amountOfStockInAddresses,
						nameSlug: d.nameSlug,
						name: d.name
					}
					initializeDetailForm(initValues)
				})
			)
		} else {
			initializeEmptyForm()
		}
	}, [dispatch, id])

	// --- 6) Create a brand‐new item:
	const handleCreate = (body: IStoreItemForm) => {
		dispatch(
			createStoreItems(body, (newStoreItemId) => {
				// Navigate to “/store‐item/:newId”
				history.replace(`${t('paths:storeItemDetail|path')}/${newStoreItemId}`)
				// Update local state to “edit” mode
				setID(newStoreItemId)
				// → NOTE: removed `window.location.reload()`
			})
		)
	}

	// --- 7) Update an existing item:
	const handleUpdate = (body: IStoreItemForm) => {
		if (id === null) return
		dispatch(
			updateStoreItem(id, body, () => {
				// After onSuccess, re‐fetch detail so the form shows the latest “updatedAt”
				dispatch(
					getStoreItem(id, (response) => {
						const d = response.data
						const initValues: IStoreItemForm = {
							id: d.id,
							updatedAt:
								moment(d.updatedAt).format('D. MMM YYYY HH:mm') || '-',
							name: d.name,
							price: d.price,
							isLiquid: d.isLiquid,
							isReplaceable: d.isReplaceable,
							activeJobs: d.activeJobs,
							idealStockPercentage: d.idealStockPercentage,
							allAvailableStock: d.allAvailableStock,
							amountOfStockInAddresses: d.amountOfStockInAddresses,
							nameSlug: d.nameSlug
						}
						initializeDetailForm(initValues)
					})
				)
			})
		)
	}

	const handleSubmit = (values: IStoreItemForm) => {
		if (id === null) {
			handleCreate(values)
		} else {
			handleUpdate(values)
		}
	}

	return (
		<div className="page-wrapper">
			<Spin spinning={isLoading}>
				{/*
          ↓ Only render the form when you know if you’re creating (id===null)
             or editing (id !== null). This prevents “id!” from being null.
        */}
				{id === null ? (
					<StoreItemForm
						onSubmit={handleSubmit}
						isCreate={true}
						id={id!} />
				) : (
					<StoreItemForm
						isCreate={false}
						id={id}
						onSubmit={handleSubmit}
					/>
				)}
			</Spin>
		</div>
	)
}

export default StoreItemDetail
